import { useState, useRef, useCallback, useEffect } from 'react';
import threadService from '../services/threadService';

  /**
   * Hook para manejar la grabación de audio
   * Extrae toda la lógica de grabación del componente Chat.jsx
   */
  export const useAudioRecording = (onAudioReady?: (audioBlob: Blob) => void) => {
    // Estados de grabación
    const [isRecording, setIsRecording] = useState(false);
    const [permissionNeeded, setPermissionNeeded] = useState(false);
    const [audioError, setAudioError] = useState<string | null>(null);
    const [isProcessingAudio, setIsProcessingAudio] = useState(false);

    // Referencias para el MediaRecorder
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    // ✅ FIX: Track MediaStream for cleanup on unmount
    const mediaStreamRef = useRef<MediaStream | null>(null);

  // ✅ FIX: Cleanup MediaStream on unmount to prevent memory leaks
  // If component unmounts DURING recording, this ensures microphone is released
  useEffect(() => {
    return () => {
      // Stop all tracks on the MediaStream
      if (mediaStreamRef.current) {
        console.log('[useAudioRecording] Cleanup: Stopping MediaStream tracks on unmount');
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      // Stop MediaRecorder if still active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('[useAudioRecording] Cleanup: Stopping MediaRecorder on unmount');
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
        mediaRecorderRef.current = null;
      }
    };
  }, []);

  /**
   * Obtener permisos del micrófono
   */
  const getMicrophonePermission = useCallback(async (): Promise<MediaStream | null> => {
    if (!('MediaRecorder' in window)) {
      setAudioError('MediaRecorder no es soportado por este navegador.');
      return null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionNeeded(false);
      setAudioError(null);
      return stream;
    } catch (err) {
      console.error('Error al obtener permiso del micrófono:', err);
      setAudioError('Permiso de micrófono denegado o no disponible.');
      setPermissionNeeded(true);
      return null;
    }
  }, []);

  /**
   * Iniciar grabación de audio
   */
  const startRecording = useCallback(async (): Promise<void> => {
    const stream = await getMicrophonePermission();
    if (!stream) return;

    // ✅ FIX: Store stream in ref for cleanup on unmount
    mediaStreamRef.current = stream;

    setIsRecording(true);
    setAudioError(null);
    audioChunksRef.current = []; // Limpiar chunks anteriores

    // Selección robusta de MIME type
    let mimeType: string | undefined = 'audio/webm;codecs=opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      console.warn('audio/webm;codecs=opus no soportado, intentando audio/ogg...');
      mimeType = 'audio/ogg;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn('audio/ogg;codecs=opus no soportado, usando default.');
        mimeType = undefined; // Usar el default del navegador
      }
    }
    const options: MediaRecorderOptions = mimeType ? { mimeType } : {};

    try {
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log("[useAudioRecording] MediaRecorder.onstop triggered.");
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        console.log("[useAudioRecording] Creating Blob with type:", mimeType);
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log("[useAudioRecording] Blob created, size:", audioBlob.size);
        
        if (audioBlob.size === 0) {
          console.warn("[useAudioRecording] Audio Blob is empty, skipping send.");
          setAudioError("La grabación resultó en un archivo vacío.");
          setIsProcessingAudio(false);
        } else {
          // Notificar al componente padre que hay audio listo para enviar
          // El componente padre debe manejar el envío
          if (onAudioReady) {
            onAudioReady(audioBlob);
          }
        }
        
        // Liberar el stream y limpiar recorder
        stream.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null; // ✅ FIX: Clear ref after cleanup
        mediaRecorderRef.current = null;
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', (event as any).error);
        setAudioError('Ocurrió un error durante la grabación.');
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null; // ✅ FIX: Clear ref after cleanup
        mediaRecorderRef.current = null;
      };

      mediaRecorderRef.current.start();
    } catch (recorderError) {
      console.error("Error al crear MediaRecorder:", recorderError);
      setAudioError("No se pudo iniciar la grabación.");
      setIsRecording(false);
      stream.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null; // ✅ FIX: Clear ref after cleanup
    }
  }, [getMicrophonePermission]);

  /**
   * Detener grabación de audio
   */
  const stopRecording = useCallback((): void => {
    console.log("[useAudioRecording] stopRecording called.");
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      console.log("[useAudioRecording] MediaRecorder stopped. Waiting for onstop event...");
    }
  }, [isRecording]);

  /**
   * Limpiar error de audio
   */
  const clearAudioError = useCallback((): void => {
    setAudioError(null);
  }, []);

  /**
   * Limpiar necesidad de permiso
   */
  const clearPermissionNeeded = useCallback((): void => {
    setPermissionNeeded(false);
  }, []);

  /**
   * Establecer estado de procesamiento de audio
   */
  const setProcessingAudio = useCallback((processing: boolean): void => {
    setIsProcessingAudio(processing);
  }, []);

  return {
    // Estados
    isRecording,
    permissionNeeded,
    audioError,
    isProcessingAudio,
    
    // Funciones
    startRecording,
    stopRecording,
    clearAudioError,
    clearPermissionNeeded,
    setProcessingAudio,
    
    // Función callback para cuando el audio esté listo
    // El componente padre debe proporcionar esta función
    onAudioReady: null as ((audioBlob: Blob) => void) | null,
  };
};

/**
 * Hook extendido que incluye el envío automático al servidor
 * Mantiene la compatibilidad con el código existente
 */
export const useAudioRecordingWithServer = (
  currentThread: any,
  setCurrentThread: (thread: any) => void,
  setThreads: (threads: any[]) => void,
  setMessages: (messages: any[]) => void,
  setProcessingStatus: (status: any) => void,
  processingStatus: any
) => {
  const {
    isRecording,
    permissionNeeded,
    audioError,
    isProcessingAudio,
    startRecording,
    stopRecording,
    clearAudioError,
    clearPermissionNeeded,
    setProcessingAudio,
  } = useAudioRecording();

  /**
   * Enviar audio al servidor (misma lógica que en Chat.jsx)
   */
  const sendAudioToServer = useCallback(async (audioBlob: Blob): Promise<void> => {
    // Comprobación temprana para evitar trabajo innecesario si ya estamos procesando
    if (isProcessingAudio) {
      console.warn("[useAudioRecordingWithServer] sendAudioToServer: Ya se está procesando un audio, aborting.");
      return;
    }
    if (currentThread && processingStatus[currentThread.id]) {
      console.warn("[useAudioRecordingWithServer] sendAudioToServer: Thread actual ocupado (procesando mensaje), aborting.");
      return;
    }

    console.log(`[useAudioRecordingWithServer] sendAudioToServer iniciando. Blob size: ${audioBlob.size}`);
    setProcessingAudio(true);
    clearAudioError();

    let targetThreadId = currentThread?.id;
    let isNewThread = false;
    
    try {
      // 1. Crear nuevo thread si no existe uno activo
      if (!targetThreadId) {
        isNewThread = true;
        const defaultTitle = `Mensaje de Audio ${new Date().toLocaleTimeString()}`;
        console.log('[useAudioRecordingWithServer] No hay thread activo. Creando nuevo thread con título:', defaultTitle);
        const newThread = await threadService.createThread(defaultTitle);
        if (!newThread || !newThread.id) {
          throw new Error("Fallo al crear un nuevo thread para el audio.");
        }
        targetThreadId = newThread.id;
        // Actualizar estado global y local
        setCurrentThread(newThread);
        // NOTA: setThreads espera el array completo, no una función updater
        setThreads([newThread]); // Si necesitas mantener los threads previos, deberás obtenerlos antes
        console.log('[useAudioRecordingWithServer] Nuevo thread creado y seleccionado:', newThread.id);
        // Limpiar mensajes existentes si los hubiera
        setMessages([]);
      }

      // 2. Marcar el hilo como procesando ANTES de enviar el audio
      setProcessingStatus((prev: any) => ({ ...prev, [targetThreadId]: true }));

      // 3. Enviar el audio al backend
      console.log(`[useAudioRecordingWithServer] Calling threadService.sendAudioMessage for thread ${targetThreadId}...`);
      const savedTextMessage = await threadService.sendAudioMessage(targetThreadId, audioBlob) as any; // TODO: tipar correctamente
      console.log("[useAudioRecordingWithServer] threadService.sendAudioMessage successful. Response:", savedTextMessage);
      
      // 4. Añadir mensaje transcrito a la UI
      if (savedTextMessage && savedTextMessage.id) {
        const formattedMessage = {
          id: savedTextMessage.id,
          thread_id: savedTextMessage.thread_id,
          user_id: savedTextMessage.user_id,
          content: savedTextMessage.content,
          sender: savedTextMessage.sender,  // Backend now returns 'sender' directly ('usuario' | 'assistant')
          created_at: savedTextMessage.created_at,
          timestamp: new Date(savedTextMessage.created_at),
          message_type: 'text',
          color: savedTextMessage.color
        };
        // NOTA: setMessages espera el array completo, no una función updater
        setMessages([formattedMessage]); // Si necesitas mantener los mensajes previos, deberás obtenerlos antes
        console.log("[useAudioRecordingWithServer] Mensaje transcrito añadido a la UI (formateado).");
      } else {
        console.error("[useAudioRecordingWithServer] La respuesta de sendAudioMessage no fue válida.", savedTextMessage);
        throw new Error("El servidor no devolvió un mensaje válido tras la transcripción.");
      }

    } catch (error) {
      console.error('[useAudioRecordingWithServer] Error en sendAudioToServer:', error);

      // ✅ CRITICAL: Clean processingStatus on error
      // If audio send fails, we need to unlock the input
      // On success, processingStatus will be cleared when:
      // 1. agent_message arrives (Chat.jsx handleMessage)
      // 2. agentStatus updates to {agentName:"", currentTask:""} (Chat.jsx useEffect)
      if (targetThreadId) {
        console.log(`[useAudioRecordingWithServer] ❌ Error, clearing processingStatus for thread ${targetThreadId}`);
        setProcessingStatus((prev: any) => ({
          ...prev,
          [targetThreadId]: false
        }));
      }

      throw error;
    } finally {
      console.log("[useAudioRecordingWithServer] sendAudioToServer finished, setting isProcessingAudio=false.");
      setProcessingAudio(false);
    }
  }, [
    currentThread,
    processingStatus,
    isProcessingAudio,
    setProcessingAudio,
    clearAudioError,
    setCurrentThread,
    setThreads,
    setMessages,
    setProcessingStatus
  ]);

  // Configurar el callback para enviar automáticamente al servidor
  const audioRecordingHook = useAudioRecording();
  audioRecordingHook.onAudioReady = sendAudioToServer;

  return {
    ...audioRecordingHook,
    sendAudioToServer,
  };
}; 